import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getSettings,
  updateSettings,
} from "../../../services/settingsServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import { FaSave, FaTimes } from "react-icons/fa";

import {
  uploadSettingsDefaultLicCot,
  toAbsolutePhotoUrl,
} from "../../../services/settingsPhotoServices";

const PaiementSettingsForm = () => {
  const [settings, setSettings] = useState({
    max_payments: 3,
    payment_interval: 1,
    photo_url_default_lic_cot: "",
  });

  const [loading, setLoading] = useState(true);

  // Upload modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [outputFormat, setOutputFormat] = useState("webp"); // webp|jpg|png
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // cache-busting image preview
  const [imgVersion, setImgVersion] = useState(0);

  const uploadDialogRef = useRef(null);

  const withBust = (url, v) =>
    url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();
        if (Array.isArray(settingsData) && settingsData.length > 0) {
          setSettings((prev) => ({ ...prev, ...settingsData[0] }));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...settings,
      max_payments: Number(settings.max_payments) || 1,
      payment_interval: Number(settings.payment_interval) || 1,
    };
    try {
      await updateSettings(payload);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  // Upload handlers
  const openUploadModal = () => {
    setSelectedFile(null);
    setOutputFormat("webp");
    setUploadProgress(0);
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (uploading) return;
    setIsUploadModalOpen(false);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setSelectedFile(f);

    if (!f) return;

    const okType = /^image\/(png|jpe?g|webp)$/i.test(f.type);
    if (!okType) {
      toast.error("Format non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }

    if (f.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadSettingsDefaultLicCot(selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          /* ... */
        },
      });
      const newUrl = updated?.url || updated?.imageUrl || "";

      if (!newUrl) {
        toast.error("Réponse upload invalide (url manquante).");
        return;
      }

      setSettings((prev) => ({
        ...prev,
        photo_url_default_lic_cot: newUrl,
      }));

      setImgVersion((v) => v + 1);
      toast.success("Image par défaut mise à jour avec succès !");
      closeUploadModal();
    } catch (error) {
      console.error(
        "Erreur lors de l'upload de l'image :",
        error?.response?.data || error,
      );
      toast.error("Erreur lors de l'upload de l'image.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ESC + focus modale
  useEffect(() => {
    if (!isUploadModalOpen) return;
    const onEsc = (e) => e.key === "Escape" && closeUploadModal();
    window.addEventListener("keydown", onEsc);
    const t = setTimeout(() => {
      uploadDialogRef.current?.querySelector("input[type='file']")?.focus();
    }, 0);
    return () => {
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadModalOpen, uploading]);

  const imageSrc = useMemo(() => {
    const raw =
      settings.photo_url_default_lic_cot?.trim() || "/img/no-image.jpg";
    const abs = toAbsolutePhotoUrl(raw) || "/img/no-image.jpg";
    return withBust(abs, imgVersion);
  }, [settings.photo_url_default_lic_cot, imgVersion]);

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-64 bg-white/25 rounded animate-pulse" />
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 rounded-xl bg-slate-100/80 dark:bg-slate-800/40 animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Paiements — Paramètres & Image par défaut
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Définissez le nombre d’échéances, l’intervalle et l’image par
                défaut du règlement.
              </p>
            </div>
            <Button
              text="Enregistrer"
              icon={FaSave}
              onClick={handleSubmit}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
            />
          </div>

          {/* CONTENT */}
          <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Échéancier de paiement
                </h3>

                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nombre maximum de paiements (Licence-Assurance & Cotisation)
                </label>
                <select
                  name="max_payments"
                  value={settings.max_payments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {[...Array(5)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>

                <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Espacement des paiements (en mois)
                </label>
                <select
                  name="payment_interval"
                  value={settings.payment_interval}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {[1, 2, 3].map((interval) => (
                    <option key={interval} value={interval}>
                      {interval}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end">
                <Button
                  text="Mettre à jour"
                  type="submit"
                  className="!rounded-xl gap-2"
                  icon={FaSave}
                />
              </div>
            </form>

            {/* Carte Image */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
              <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Image par défaut — Règlement d’inscription
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr,200px] gap-4 items-start">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    URL actuelle (lecture seule)
                  </label>
                  <input
                    type="text"
                    value={settings.photo_url_default_lic_cot || ""}
                    readOnly
                    className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700
                               bg-white/60 dark:bg-slate-900/60 px-3 py-2 text-slate-900 dark:text-slate-100
                               focus:outline-none select-all cursor-text"
                    onFocus={(e) => e.target.select()}
                    placeholder="— non défini —"
                  />

                  <div className="mt-3">
                    <Button
                      text="Changer l'image"
                      onClick={openUploadModal}
                      className="!rounded-xl gap-2"
                    />
                  </div>

                  <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                    Formats : JPG/PNG/WebP • max 10 Mo.
                  </p>
                </div>

                <div className="relative">
                  <img
                    src={imageSrc}
                    alt="Aperçu image par défaut"
                    className="w-full h-40 sm:h-48 object-cover rounded-xl shadow ring-2 ring-white dark:ring-slate-800"
                    onError={(e) => {
                      e.currentTarget.src = "/img/no-image.jpg";
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modale Upload */}
      {isUploadModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Changer l'image par défaut"
          onClick={(e) => e.currentTarget === e.target && closeUploadModal()}
        >
          <div
            ref={uploadDialogRef}
            className="relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
              <div>
                <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
                  Changer l’image par défaut
                </h3>
                <p className="text-white/90 text-xs sm:text-sm mt-1">
                  JPG/WEBP recommandé • max 10 Mo.
                </p>
              </div>
              <button
                onClick={closeUploadModal}
                disabled={uploading}
                className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white disabled:opacity-60"
                aria-label="Fermer"
                type="button"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            <form
              onSubmit={handleUploadSubmit}
              className="p-4 sm:p-6 space-y-4"
            >
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFileChange}
                disabled={uploading}
                className="w-full"
              />

              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Format
                </span>
                <select
                  className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  disabled={uploading}
                >
                  <option value="webp">WEBP (recommandé)</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                </select>
              </div>

              {uploading && (
                <div>
                  <progress
                    className="progress w-full"
                    value={uploadProgress}
                    max="100"
                  />
                  <div className="text-xs mt-1">{uploadProgress}%</div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  disabled={uploading}
                  className="rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <FaTimes /> Fermer
                </button>

                <Button
                  text={uploading ? "Transfert…" : "Sauvegarder"}
                  type="submit"
                  icon={FaSave}
                  className="!rounded-xl gap-2"
                  isLoading={uploading}
                  disabled={uploading || !selectedFile}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

export default PaiementSettingsForm;
